import React from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle,
  Shield,
  Zap,
  Clock,
  FileCheck,
  Users,
  ArrowRight,
  Building2,
  Scale,
  Landmark,
  Heart,
  UserCheck,
  Share2
} from 'lucide-react';

const Home = () => {
  const features = [
    {
      icon: Zap,
      title: '3-5 Minute Verification',
      description: 'Your clients complete KYC/KYB in minutes, not days. Mobile-first experience with real-time guidance.'
    },
    {
      icon: Shield,
      title: 'Audit-Grade Evidence',
      description: 'Every check is logged, timestamped, and immutable. Export complete audit packs with one click.'
    },
    {
      icon: FileCheck,
      title: 'No Repeated KYC',
      description: 'Once verified, clients can share their verification with other companies securely.'
    },
    {
      icon: Clock,
      title: 'Automated Monitoring',
      description: 'Get alerts when client circumstances change. Stay compliant without manual checks.'
    }
  ];

  const steps = [
    {
      number: '01',
      title: 'Invite your client',
      description: 'Enter name and email. Choose KYC or KYB, select the level, and send.'
    },
    {
      number: '02',
      title: 'They verify in minutes',
      description: 'Client receives secure link, completes verification on their mobile device.'
    },
    {
      number: '03',
      title: 'You receive audit-ready file',
      description: 'Review, assess risk, approve or decline. Export complete audit pack.'
    }
  ];

  const industries = [
    { icon: Scale, name: 'Law Companies' },
    { icon: Building2, name: 'Accountants' },
    { icon: Landmark, name: 'Financial Advisors' },
    { icon: Users, name: 'Corporate Services' }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative bg-navy-900 text-white overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 to-transparent"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32 relative">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Client due diligence,{' '}
              <span className="text-blue-400">without the paperwork.</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Request, verify, and monitor KYC & KYB across UK, EU, and US — with one
              secure platform. Built for law companies, accountants, and financial services.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/login"
                className="inline-flex items-center justify-center px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                Request a demo
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
              <Link
                to="/features"
                className="inline-flex items-center justify-center px-6 py-3 bg-white/10 text-white rounded-lg font-medium hover:bg-white/20 transition-colors"
              >
                See how it works
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">How it works</h2>
            <p className="text-lg text-gray-600">Simple, three-step process</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="relative">
                <div className="text-6xl font-bold text-blue-100 mb-4">{step.number}</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{step.title}</h3>
                <p className="text-gray-600">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Why companies choose Acquiva</h2>
            <p className="text-lg text-gray-600">Everything you need for compliant client onboarding</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600 text-sm">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Who it's for</h2>
            <p className="text-lg text-gray-600">Built for professional services companies</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {industries.map((industry, index) => {
              const Icon = industry.icon;
              return (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-navy-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-8 h-8 text-navy-700" />
                  </div>
                  <h3 className="font-medium text-gray-900">{industry.name}</h3>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Clients Love It */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Clients Love It</h2>
            <p className="text-lg text-gray-600">A verification experience designed with your clients in mind</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <Clock className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Respectful of Their Time</h3>
              <p className="text-gray-600">
                We offer the most low-touch, easy-to-use verification possible. Your clients complete
                the process in minutes, not hours, with a mobile-first experience that respects their time.
              </p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <UserCheck className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">They're in Control</h3>
              <p className="text-gray-600">
                Clients control their data and choose what to share, with whom, and for how long.
                From basic verification status to more personal details — it's always their choice.
              </p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <Share2 className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Verify Once, Share Many</h3>
              <p className="text-gray-600">
                Once verified, clients can choose to share their verification status with other companies
                using the platform. No more repeating the same process for every new relationship.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Trust & Compliance */}
      <section className="py-20 bg-navy-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">Trust & Compliance</h2>
              <p className="text-gray-300 mb-8">
                We support your regulatory obligations — you stay in control. 
                No black boxes, no automated decisions.
              </p>
              <ul className="space-y-4">
                {[
                  'Encrypted data storage',
                  'Only shared with permission',
                  'Revocable anytime',
                  'Complete audit logging',
                  'GDPR compliant',
                  'UK, EU, and US coverage'
                ].map((item, index) => (
                  <li key={index} className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span className="text-gray-300">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white/5 rounded-2xl p-8">
              <div className="space-y-6">
                <div className="flex items-center justify-between p-4 bg-white/10 rounded-lg">
                  <span className="font-medium">Data encrypted at rest</span>
                  <Shield className="w-5 h-5 text-green-400" />
                </div>
                <div className="flex items-center justify-between p-4 bg-white/10 rounded-lg">
                  <span className="font-medium">SOC 2 Type II certified</span>
                  <CheckCircle className="w-5 h-5 text-green-400" />
                </div>
                <div className="flex items-center justify-between p-4 bg-white/10 rounded-lg">
                  <span className="font-medium">Regular penetration testing</span>
                  <CheckCircle className="w-5 h-5 text-green-400" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-blue-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to streamline your compliance?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Join companies already using Acquiva for client due diligence.
          </p>
          <Link
            to="/login"
            className="inline-flex items-center justify-center px-8 py-4 bg-white text-blue-600 rounded-lg font-medium hover:bg-gray-100 transition-colors"
          >
            Get started today
            <ArrowRight className="ml-2 w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
